// The `m8t platform status --verify` gateway comparison, kept pure and separate
// from the command so both of its branches are directly testable.
//
// The install now deploys the gateway BY DIGEST, so the live Container App image
// usually carries no tag at all. The old probe derived a "tag" from the image with
// `image.split(":").pop()`, which on a digest ref returns the 64-hex digest body and
// renders it as though it were a version — producing a permanent, meaningless
// "match: no" on every digest-pinned install.
//
// Two rules govern what this may print:
//   1. Digest for what RUNS, tag for what it is CALLED. The stamp keeps the tag
//      ladder (the web's update-availability check settles against it); only the
//      binding is a digest.
//   2. A verdict may only be claimed off a reading that actually happened. Echoing
//      the stamp back at itself would render a confident "yes" for a gateway nobody
//      could reach — exactly the failure a verify pass exists to surface.

import { parseImageRef } from "./platform-update.js";

export interface GatewayBinding {
  stampTag: string;
  stampDigest: string | undefined;
  /** How the live deployment pins its image, as actually read off it. */
  live: { kind: "digest"; digest: string } | { kind: "tag"; tag: string } | { kind: "unread" };
  comparedBy: "digest" | "tag" | "none";
  match: boolean | "unknown";
}

/**
 * Compare the stamp against the live deployment. `liveImage` is the raw
 * `properties.template.containers[0].image` value, or undefined when the read
 * failed — undefined is a distinct outcome from "read something unexpected", and
 * only the former yields "unknown" without a comparison.
 */
export function compareGatewayBinding(args: {
  stampTag: string;
  stampDigest: string | undefined;
  liveImage: string | undefined;
}): GatewayBinding {
  const base = { stampTag: args.stampTag, stampDigest: args.stampDigest };

  if (!args.liveImage) {
    return { ...base, live: { kind: "unread" }, comparedBy: "none", match: "unknown" };
  }

  const parsed = parseImageRef(args.liveImage);

  // `comparedBy` names a comparison that HAPPENED. Reading the live side is only
  // half of one — with no counterpart recorded in the stamp there is nothing to
  // compare against, and claiming a method for a comparison that never ran is the
  // same unearned claim as reporting a match that was never established.
  if (parsed.digest) {
    // Digest-vs-digest is strictly stronger than the tag comparison it replaces:
    // it establishes the bytes, where a tag only ever established a label.
    const comparable = Boolean(args.stampDigest);
    return {
      ...base,
      live: { kind: "digest", digest: parsed.digest },
      comparedBy: comparable ? "digest" : "none",
      match: comparable ? parsed.digest === args.stampDigest : "unknown",
    };
  }

  if (parsed.tag) {
    const comparable = args.stampTag !== "unknown" && args.stampTag !== "";
    return {
      ...base,
      live: { kind: "tag", tag: parsed.tag },
      comparedBy: comparable ? "tag" : "none",
      match: comparable ? parsed.tag === args.stampTag : "unknown",
    };
  }

  return { ...base, live: { kind: "unread" }, comparedBy: "none", match: "unknown" };
}

const LABEL = (s: string) => `  ${s}`.padEnd(22, " ");

/**
 * Render the `live verify:` block. `fmt` colourises the verdict token only —
 * defaulting to identity so tests assert the copy, not ANSI escapes.
 */
export function renderLiveVerify(
  v: GatewayBinding,
  apiVersion: string,
  fmt: { ok: (s: string) => string; warn: (s: string) => string } = { ok: (s) => s, warn: (s) => s },
): string[] {
  const stamp = v.stampDigest ? `${v.stampTag} · ${v.stampDigest}` : v.stampTag;

  let live: string;
  switch (v.live.kind) {
    case "digest":
      live = v.live.digest;
      break;
    case "tag":
      // The parenthetical is the only thing that tells an operator their install
      // predates digest-pinning and that its bytes were never established. It must
      // appear here and never on the digest branch.
      live = `${v.live.tag}  (tag-pinned — bytes not verified)`;
      break;
    default:
      live = "unknown";
  }

  const verdict =
    v.match === "unknown" ? fmt.warn("unknown") : v.match ? fmt.ok("yes") : fmt.warn("no");
  // No "compared by" clause when nothing was compared — naming a comparison method
  // for a comparison that never ran is the same class of claim this block exists to
  // stop making.
  const how = v.comparedBy === "none" ? "" : `  (compared by ${v.comparedBy})`;

  return [
    `${LABEL("stamp gateway:")}${stamp}`,
    `${LABEL("live gateway:")}${live}`,
    `${LABEL("match:")}${verdict}${how}`,
    `${LABEL("/api/version:")}${apiVersion}`,
  ];
}
